import * as api from '@bakkt/api'
import {PortalPermission, PartnerRolePermissionEnum, PortalRole, User} from './AppState'
export {PortalPermission, PartnerRolePermissionEnum, PortalRole}

export const PORTAL_PERMISSIONS_LIST = Object.entries(api.ApiMerchantPortalRolePermissionsEnum).map(e => e[1])
export const PARTNER_PORTAL_PERMISSIONS_LIST = Object.entries(api.ApiPortalRolePermissionsEnum).map(e => e[1])

class PermissionManager {
	currentUser: User | null = null

	/*
    returns boolean whether user has ANY of the required permissions
    @param userPermissions - array containing the user's permissions
    @param requiredPermissions - array containing permissions that are allowed to access
    */
	canAccess(
		userPermissions: (PortalPermission | PartnerRolePermissionEnum)[],
		requiredPermissions: (PortalPermission | PartnerRolePermissionEnum)[],
	) {
		if (userPermissions.find(userPermission => requiredPermissions.indexOf(userPermission) >= 0) != null)
			return true
		return false
	}

	/*
    state-dependent wrapper around canAccess that uses the user currently in the store
    returns boolean whether the operation is allowed for the user
    @param requiredPermissions - any allowed permissions
    */
	userAllowed(requiredPermissions: (PortalPermission | PartnerRolePermissionEnum)[]) {
		const user = this.getCurrentUser()
		if (user == null) return false
		return this.canAccess(user.permissions, requiredPermissions)
	}

	getCurrentUserPermissions() {
		return this.currentUser && this.currentUser.permissions
	}

	getCurrentUser() {
		return this.currentUser
	}

	getAllPartnersObjCurrentUserCanAccess(): {[key: string]: string} {
		return this.getCurrentUser()?.partners
	}
	getAllPartnersArrayCurrentUserCanAccess(): {id: string; name: string}[] {
		const partners = this.getCurrentUser()?.partners
		const partnersArr: {id: string; name: string}[] = []

		for (const [id, name] of Object.entries(partners)) {
			partnersArr.push({id, name})
		}
		return partnersArr
	}

	setCurrentUser(user: User) {
		this.currentUser = user
	}

	isBakktUser = () => {
		const user = this.getCurrentUser()
		return user.email.endsWith('bakkt.com')
	}

	// Returns boolean whether user has ANY of the required CSR permissions
	isAnyCSRAdminPermission() {
		return this.userAllowed(Object.values(PortalPermission))
	}

	canAcceptDispute() {
		return this.userAllowed([PortalPermission.DISPUTE_ACCEPT])
	}

	canRejectDispute() {
		return this.userAllowed([PortalPermission.DISPUTE_REJECT])
	}

	canAddComment() {
		return this.userAllowed([PortalPermission.DISPUTE_ADD_COMMENT])
	}

	canTakeDisputeAction() {
		return this.userAllowed([
			PortalPermission.DISPUTE_ACCEPT,
			PortalPermission.DISPUTE_REJECT,
			PortalPermission.DISPUTE_ADD_COMMENT,
		])
	}

	canCancelTransaction() {
		return this.userAllowed([PortalPermission.TRANSACTION_CANCEL])
	}

	canReverseTransaction() {
		return this.userAllowed([PortalPermission.TRANSACTION_REVERSE])
	}

	canTakeTransactionAction() {
		return this.userAllowed([PortalPermission.TRANSACTION_CANCEL, PortalPermission.TRANSACTION_REVERSE])
	}

	canAddUser() {
		return this.userAllowed([PortalPermission.USERS_CREATE])
	}

	canUpdateUser() {
		return this.userAllowed([PortalPermission.USERS_UPDATE])
	}
	canCreateAndUpdateRole() {
		return this.userAllowed([api.ApiPortalRolePermissionsEnum.ROLE_CREATE_UPDATE])
	}
	canViewIdDocSubmission() {
		return this.userAllowed([api.ApiPortalRolePermissionsEnum.ID_DOC_SUBMISSION_VIEW])
	}
	isSupportedOperation(operation: api.ApiPartnerUserInfoSupportedOperationsEnum) {
		const user = this.getCurrentUser()
		return user.supportedOperations?.includes(operation)
	}
}

export default new PermissionManager()
